import { FC, ReactElement, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import Typography from 'src/components/atoms/Typography'
import ProjectGrid from 'src/components/organisms/ProjectGrid'
import DashboardHeader from 'src/components/molecules/DashboardHeader'
import Loader from 'src/components/atoms/Loader'
import { JumpingDots } from 'src/components/atoms/JumpingDots'
import { Project, ProjectViewMode } from 'src/types/projects'
// import RequestProjectDialog from 'src/components/organisms/dialogs/RequestProjectDialog'
import { IconSearch, IconWorldUpload } from '@tabler/icons-react'
import { Input } from 'src/components/atoms/inputs/Input'
import FilterDropdown from 'src/components/atoms/FilterDropDown'
import { PrimaryButton } from 'src/components/atoms/buttons/PrimaryButton'
import EmptyIllustration from '../../assets/emty-illustration.svg?react'
import { PROJECTS_PAGE_CONTENT } from '../../enviroment/constants/constants'
import { ProjectType } from '../../types/type'
import { ProjectStage } from '../../types/stage'
import { UserRole } from '../../types/user'
import { ProjectActionPanel } from 'src/components/organisms/ProjectActionPanel'
import { ProjectSelectionBar } from 'src/components/organisms/ProjectSelectionBar'

interface ProjectsTemplateProps {
  mode: ProjectViewMode
  projects: Project[]
  searchTerm: string
  setSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  isLoading: boolean
  error: unknown
  selectedTypesIds: string[]
  setSelectedTypesIds: (ids: string[]) => void
  selectedStagesIds: string[]
  selectedLocations: string[]
  setSelectedStagesIds: (ids: string[]) => void
  setSelectedLocations: (ids: string[]) => void
  isFetching: boolean
  fetchNextProjectsPage: () => void
  hasMoreProjects?: boolean
  isFetchingMoreProjects?: boolean
  types: ProjectType[] | undefined
  onTypesScrollEnd?: () => void
  stages: ProjectStage[] | undefined
  locations: ProjectType[] | undefined
  onOpenProject: (project: Project) => void
  role: UserRole
  onCreateProjectClick: () => void
  selectedIds: Set<string>
  onToggleSelect?: (id: string) => void
  onSelectAll?: () => void
  onDeselectAll?: () => void
  onDeleteSelected?: () => void
}

const ProjectsTemplate: FC<ProjectsTemplateProps> = ({
  mode,
  projects,
  isLoading,
  error,
  isFetching,
  fetchNextProjectsPage,
  hasMoreProjects,
  isFetchingMoreProjects,
  types,
  onTypesScrollEnd,
  stages,
  searchTerm,
  locations,
  selectedLocations,
  selectedStagesIds,
  selectedTypesIds,
  setSelectedLocations,
  setSelectedStagesIds,
  setSelectedTypesIds,
  setSearchTerm,
  onOpenProject,
  role,
  onCreateProjectClick,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onDeleteSelected,
}): ReactElement => {
  const pageContent = PROJECTS_PAGE_CONTENT[mode]
  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({ threshold: 0, rootMargin: '100px' })

  // Fetch the next page once the sentinel scrolls into view.
  useEffect(() => {
    if (loadMoreInView && hasMoreProjects && !isFetchingMoreProjects) {
      fetchNextProjectsPage()
    }
  }, [loadMoreInView, hasMoreProjects, isFetchingMoreProjects, fetchNextProjectsPage])

  return (
    <div className="w-full pb-24">
      <DashboardHeader
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        rightSide={
          mode === 'public' || role === UserRole.VIEWER ? undefined : (
            <PrimaryButton
              size="sm"
              className="min-w-[160px]"
              onClick={onCreateProjectClick}
              rightIcon={<IconWorldUpload size={20} />}
            >
              Create project
            </PrimaryButton>
          )
        }
      />

      {/* Search and Filters Section */}
      <div className="flex flex-col space-y-4 px-4 pt-4 md:flex-row md:items-center md:justify-between md:space-y-0 md:px-8">
        <Input
          containerClassName="w-full md:w-[60%] lg:w-[50%]"
          icon={<IconSearch className="text-light-green-500" />}
          className="h-11 border-gray-700 bg-gray-800 text-gray-50"
          placeholder="Search by keyword..."
          onChange={setSearchTerm}
          value={searchTerm}
        />

        {/* Filters */}
        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
          {stages && (
            <FilterDropdown
              type="Stages"
              options={stages}
              selectedIds={selectedStagesIds}
              onChange={setSelectedStagesIds}
              className="w-full min-w-[200px] sm:w-[200px]"
            />
          )}
          {types && (
            <FilterDropdown
              type="Types"
              options={types}
              selectedIds={selectedTypesIds}
              onChange={setSelectedTypesIds}
              className="w-full min-w-[200px] sm:w-[200px]"
              onScrollEnd={onTypesScrollEnd}
            />
          )}
          {locations && (
            <FilterDropdown
              type="Locations"
              options={locations}
              selectedIds={selectedLocations}
              onChange={setSelectedLocations}
              className="w-full min-w-[200px] sm:w-[200px]"
            />
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex h-[50vh] items-center justify-center pt-10 text-light-green-500">
          <Loader />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !isFetching && !error && projects.length === 0 && (
        <div className="mt-10 flex h-full flex-col items-center justify-center space-y-2 px-4 text-center text-light-green-50">
          <Typography variant="3xl-bold">{pageContent.emptyTitle}</Typography>
          <Typography variant="base">{pageContent.emptySubtitle}</Typography>
          <EmptyIllustration />
        </div>
      )}

      {/* Selection bar — only on tenant tab */}
      {mode === 'tenant' && !isLoading && !error && projects.length > 0 && onSelectAll && onDeselectAll && (
        <ProjectSelectionBar
          selectedCount={selectedIds.size}
          totalCount={projects.length}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
        />
      )}

      {/* Projects Grid */}
      {!isLoading && !error && projects.length > 0 && (
        <div className="space-y-8 p-4 pt-2 md:px-8 md:pb-8 md:pt-2">
          <ProjectGrid
            projects={projects}
            onOpenProject={onOpenProject}
            mode={mode}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
          />

          {/* Infinite scroll sentinel — fetches the next page when it enters the viewport */}
          {hasMoreProjects && (
            <div ref={loadMoreRef} className="py-2 text-center">
              {isFetchingMoreProjects && <JumpingDots className="text-light-green-50" />}
            </div>
          )}
        </div>
      )}

      {/* Floating action panel — only on tenant tab */}
      {mode === 'tenant' && selectedIds.size > 0 && onDeleteSelected && onDeselectAll && (
        <ProjectActionPanel selectedCount={selectedIds.size} onDelete={onDeleteSelected} onClear={onDeselectAll} />
      )}
    </div>
  )
}

export default ProjectsTemplate
